import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCcw, Loader2, Eye } from 'lucide-react';

interface BoundingBox {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ClickData {
  rank: number;
  x: number;
  y: number;
  product_id: string | null;
  product_label: string | null;
  timestamp_ms: number;
}

interface ImageHotspotResponseProps {
  imageUrl: string;
  boundingBoxes: BoundingBox[];
  rankingCount: number;
  onComplete: (clicks: ClickData[]) => void;
  disabled?: boolean;
  freeClickMode?: boolean;
}

const ImageHotspotResponse: React.FC<ImageHotspotResponseProps> = ({
  imageUrl,
  boundingBoxes,
  rankingCount,
  onComplete,
  disabled = false,
  freeClickMode = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [clicks, setClicks] = useState<ClickData[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [invalidClick, setInvalidClick] = useState<{ x: number; y: number; message: string } | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [isReady, setIsReady] = useState(false); // Always start masked - user must click "Show Image"

  // Reset state when question changes (imageUrl or boundingBoxes change)
  useEffect(() => {
    setClicks([]);
    setSelectedProductIds(new Set());
    setImageSize({ width: 0, height: 0 });
    setInvalidClick(null);
    setImageLoading(true); // Show loading until new image loads
    setIsReady(false); // Always start masked
    setStartTime(null); // Reset timer
    // Don't start timer until image loads and user clicks "Show"
  }, [imageUrl, rankingCount]);

  // Reset selection state when bounding box IDs change (for duplicated questions with same image)
  const boundingBoxesKey = JSON.stringify(boundingBoxes.map(b => b.id));
  useEffect(() => {
    setClicks([]);
    setSelectedProductIds(new Set());
    setInvalidClick(null);
    setIsReady(false);
    setStartTime(null);
  }, [boundingBoxesKey]);

  // Auto-hide invalid click message after 1.5 seconds
  useEffect(() => {
    if (invalidClick) {
      const timer = setTimeout(() => setInvalidClick(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [invalidClick]);

  // Handle image load - just set dimensions, timer starts when user clicks "Show Image"
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({ width: img.clientWidth, height: img.clientHeight });
    setImageLoading(false);
    // Timer starts only when user clicks "Show Image", not on image load
  };

  // Handle "Show Image" button click - unmasks image and starts timer
  const handleShowImage = () => {
    setIsReady(true);
    setStartTime(Date.now()); // Start timer when user clicks show
    // Scroll the image into center view
    setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Convert pixel to normalized coordinates
  const pixelToNormalized = useCallback((px: number, py: number) => {
    if (imageSize.width === 0 || imageSize.height === 0) return { x: 0, y: 0 };
    return {
      x: px / imageSize.width,
      y: py / imageSize.height
    };
  }, [imageSize]);

  // Convert normalized to pixel coordinates
  const normalizedToPixel = useCallback((nx: number, ny: number) => {
    return {
      x: nx * imageSize.width,
      y: ny * imageSize.height
    };
  }, [imageSize]);

  // Check if point is inside a bounding box
  const findProductAtPoint = (x: number, y: number): BoundingBox | null => {
    for (const box of boundingBoxes) {
      if (
        x >= box.x &&
        x <= box.x + box.width &&
        y >= box.y &&
        y <= box.y + box.height
      ) {
        return box;
      }
    }
    return null;
  };

  // Check if click is too close to an existing click (for free click mode)
  const isDuplicateCoordinate = (x: number, y: number): boolean => {
    const threshold = 0.02; // 2% of image dimension
    return clicks.some(c =>
      Math.abs(c.x - x) < threshold && Math.abs(c.y - y) < threshold
    );
  };

  // Handle click on image
  const handleClick = (e: React.MouseEvent) => {
    if (disabled || clicks.length >= rankingCount || !startTime) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const pixelX = e.clientX - rect.left;
    const pixelY = e.clientY - rect.top;
    const normalized = pixelToNormalized(pixelX, pixelY);

    if (freeClickMode) {
      // Free click mode: allow clicks anywhere, check for duplicate coordinates
      if (isDuplicateCoordinate(normalized.x, normalized.y)) {
        setInvalidClick({ x: pixelX, y: pixelY, message: 'Too close to existing selection' });
        return;
      }

      // Clear any invalid click message
      setInvalidClick(null);

      const timestamp_ms = Date.now() - startTime;
      const newClick: ClickData = {
        rank: clicks.length + 1,
        x: normalized.x,
        y: normalized.y,
        product_id: null,
        product_label: null,
        timestamp_ms
      };

      const newClicks = [...clicks, newClick];
      setClicks(newClicks);

      // Check if we've collected enough clicks
      if (newClicks.length >= rankingCount) {
        onComplete(newClicks);
      }
    } else {
      // Region-based mode: only allow clicks within defined bounding boxes
      const product = findProductAtPoint(normalized.x, normalized.y);

      if (!product) {
        setInvalidClick({ x: pixelX, y: pixelY, message: 'Invalid Selection' });
        return;
      }

      // Prevent selecting the same product twice
      if (selectedProductIds.has(product.id)) {
        setInvalidClick({ x: pixelX, y: pixelY, message: 'Already selected' });
        return;
      }

      // Clear any invalid click message
      setInvalidClick(null);

      const timestamp_ms = Date.now() - startTime;
      const newClick: ClickData = {
        rank: clicks.length + 1,
        x: normalized.x,
        y: normalized.y,
        product_id: product.id,
        product_label: product.label,
        timestamp_ms
      };

      const newClicks = [...clicks, newClick];
      setClicks(newClicks);
      setSelectedProductIds(prev => new Set([...prev, product.id]));

      // Check if we've collected enough clicks
      if (newClicks.length >= rankingCount) {
        onComplete(newClicks);
      }
    }
  };

  // Reset selections
  const handleReset = () => {
    setClicks([]);
    setSelectedProductIds(new Set());
    setStartTime(Date.now());
  };

  // Get rank color for visual feedback
  const getRankColor = (rank: number): string => {
    const colors = [
      'bg-teal-500',    // Rank 1
      'bg-blue-500',    // Rank 2
      'bg-purple-500',  // Rank 3
      'bg-orange-500',  // Rank 4
      'bg-pink-500',    // Rank 5
    ];
    return colors[(rank - 1) % colors.length];
  };

  const isComplete = clicks.length >= rankingCount;

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {!isReady ? (
            <span>
              Read the question carefully, then click <strong>"Show Image"</strong> when you're ready.
            </span>
          ) : (
            <>
              Click {freeClickMode ? 'anywhere ' : ''}on the image to select your <strong>top {rankingCount}</strong> {freeClickMode ? 'location' : 'choice'}{rankingCount > 1 ? 's' : ''} in order of preference.
              {clicks.length > 0 && !isComplete && (
                <span className="ml-2 text-teal-600">
                  ({clicks.length} of {rankingCount} selected)
                </span>
              )}
              {isComplete && (
                <span className="ml-2 text-green-600 font-medium">
                  ✓ Selection complete!
                </span>
              )}
            </>
          )}
        </p>
        
        {clicks.length > 0 && !disabled && isReady && (
          <button
            onClick={handleReset}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        )}
      </div>

      {/* Image with click overlay */}
      <div
        ref={containerRef}
        className={`relative select-none block w-full ${
          disabled || isComplete || !isReady ? 'cursor-default' : 'cursor-pointer'
        }`}
        onClick={isReady ? handleClick : undefined}
      >
        {/* Loading overlay */}
        {imageLoading && (
          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200 min-h-[300px] w-full py-12">
            <Loader2 className="w-10 h-10 text-teal-500 animate-spin mb-3" />
            <p className="text-sm text-gray-600">Loading image...</p>
          </div>
        )}

        {/* Ready confirmation overlay - shown when image is loaded but user hasn't clicked "Show" */}
        {!imageLoading && !isReady && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
            {/* Blur overlay */}
            <div className="absolute inset-0 backdrop-blur-xl bg-white/40 rounded-lg" />
            
            {/* Show Image button */}
            <div className="relative z-10 text-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowImage();
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg shadow-lg transition-all transform hover:scale-105 active:scale-95"
              >
                <Eye className="w-5 h-5" />
                Show Image
              </button>
              <p className="mt-3 text-sm text-gray-700 font-medium">
                Click when you're ready to view and start the timer
              </p>
            </div>
          </div>
        )}
        
        <img
          src={imageUrl}
          alt="Product selection"
          onLoad={handleImageLoad}
          loading="lazy"
          className={`w-full h-auto rounded-lg border border-gray-200 transition-all duration-300 ${
            imageLoading ? 'opacity-0' : 'opacity-100'
          } ${!isReady ? 'blur-lg' : ''}`}
          draggable={false}
        />

        {/* Show click markers for free click mode */}
        {freeClickMode && clicks.map((click) => {
          const pos = normalizedToPixel(click.x, click.y);
          return (
            <div
              key={click.rank}
              className="absolute pointer-events-none"
              style={{
                left: pos.x - 12,
                top: pos.y - 12
              }}
            >
              <div
                className={`w-6 h-6 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md border-2 border-white`}
              >
                {click.rank}
              </div>
            </div>
          );
        })}

        {/* Show bounding box highlights for region-based mode */}
        {!freeClickMode && boundingBoxes.map((box) => {
          const isSelected = selectedProductIds.has(box.id);
          const clickForBox = clicks.find(c => c.product_id === box.id);
          const pos = normalizedToPixel(box.x, box.y);
          const size = normalizedToPixel(box.width, box.height);

          return (
            <div
              key={box.id}
              className={`absolute border-2 transition-all pointer-events-none ${
                isSelected
                  ? 'border-teal-500 bg-teal-500/20'
                  : 'border-transparent hover:border-gray-300 hover:bg-gray-100/20'
              }`}
              style={{
                left: pos.x,
                top: pos.y,
                width: size.x,
                height: size.y
              }}
            >
              {/* Rank indicator on selected products */}
              {clickForBox && (
                <div
                  className={`absolute -top-3 -left-3 w-6 h-6 ${getRankColor(clickForBox.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md`}
                >
                  {clickForBox.rank}
                </div>
              )}
            </div>
          );
        })}

        {/* Invalid click feedback tooltip */}
        {invalidClick && (
          <div
            className="absolute bg-red-500 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none animate-pulse z-10"
            style={{
              left: invalidClick.x,
              top: invalidClick.y - 30,
              transform: 'translateX(-50%)'
            }}
          >
            {invalidClick.message}
          </div>
        )}
      </div>

      {/* Selection summary */}
      {clicks.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your Selections:</h4>
          <div className="space-y-1">
            {clicks.map((click) => (
              <div key={click.rank} className="flex items-center gap-2 text-sm">
                <span className={`w-5 h-5 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center`}>
                  {click.rank}
                </span>
                <span className="text-gray-700">
                  {freeClickMode || !click.product_label
                    ? `Location (${(click.x * 100).toFixed(0)}%, ${(click.y * 100).toFixed(0)}%)`
                    : click.product_label}
                </span>
                <span className="text-gray-400 text-xs">
                  ({(click.timestamp_ms / 1000).toFixed(1)}s)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageHotspotResponse;

